/**
 * HelpPanel — context-aware slide-out assistant.
 *
 * Mounted once globally (inside the router, outside <Routes>) so it is available
 * on every page of the main app. Hidden on /admin routes. Opened via the
 * HelpToggleButton in the Dashboard toolbar (see HelpContext).
 *
 * On wide screens the panel PUSHES the main content (body padding-right) rather
 * than overlaying it, so elements a step points at stay visible. The width
 * divider and the detail/index (66/33) divider are both drag-adjustable and
 * remembered in localStorage.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { HelpCircle, X, Search, Lightbulb, RefreshCw } from 'lucide-react';
import { api } from '../services/api';
import { highlightTarget, isTargetActive, inactiveHint, subscribeHelpDom } from '../help/highlightTargets';
import { useHelp } from '../help/HelpContext';
import type { HelpTopic, HelpStep, HelpContextKey } from '../help/types';

const MIN_W = 300;
const MAX_W = 680;
const MIN_RATIO = 0.25;
const MAX_RATIO = 0.85;
const WIDTH_KEY = 'help_panel_width';
const RATIO_KEY = 'help_detail_ratio';

function clampWidth(w: number) { return Math.min(MAX_W, Math.max(MIN_W, w)); }
function clampRatio(r: number) { return Math.min(MAX_RATIO, Math.max(MIN_RATIO, r)); }

function initialWidth(): number {
  const stored = Number(localStorage.getItem(WIDTH_KEY));
  if (stored) return clampWidth(stored);
  return clampWidth(window.innerWidth * 0.25);
}
function initialRatio(): number {
  const stored = Number(localStorage.getItem(RATIO_KEY));
  if (stored) return clampRatio(stored);
  return 0.66;
}

// Resume/job counts drive the default topic. Prefer the cheap canonical count
// endpoint; fall back to list lengths (the count endpoint is unavailable in the
// local stack, and lists work everywhere).
async function getProgressCounts(): Promise<{ resumes: number; jobs: number }> {
  try {
    const auth = await api.checkAuthorization();
    return { resumes: auth.counts.resumes, jobs: auth.counts.jobs };
  } catch {
    const [r, j] = await Promise.all([
      api.listResumes().catch(() => ({ resumes: [] })),
      api.listJobs(undefined, 'all').catch(() => ({ jobs: [] } as { jobs: unknown[] })),
    ]);
    return {
      resumes: (r.resumes || []).length,
      jobs: ((j as { jobs?: unknown[] }).jobs || []).length,
    };
  }
}

function resolveContextKey(resumes: number, jobs: number): HelpContextKey {
  if (resumes === 0) return 'no_resume';
  if (jobs === 0) return 'no_jobs';
  if (jobs <= 50) return 'pipeline';
  return 'tips_pool';
}

function pickDefaultTopic(topics: HelpTopic[], resumes: number, jobs: number): HelpTopic | null {
  if (topics.length === 0) return null;
  const ck = resolveContextKey(resumes, jobs);
  return topics.find((t) => t.context_key === ck) || topics[0];
}

// Inline button that names a UI control. When the control is present (or can be
// revealed by opening a parent) the button is active/orange and clicking it
// scrolls the control into view and flashes it. When the control isn't on screen
// the button goes inactive/grey with a tooltip explaining why (e.g. "Add a job
// to see this in action", or "Open the resume editor to use this"). It re-checks
// on every DOM change, so it lights up the moment its target appears.
const TARGET_TOKEN = '{{target}}';

function InlineActionButton({ step }: { step: HelpStep }) {
  const key = step.highlight_target!;
  const [, bump] = useState(0);
  useEffect(() => subscribeHelpDom(() => bump((n) => n + 1)), []);

  const active = isTargetActive(key);
  const label = step.button_label || 'Show me';

  if (active) {
    return (
      <button
        type="button"
        onClick={() => void highlightTarget(key, step.open_first)}
        className="mx-0.5 inline-flex items-center rounded-md bg-orange-500 px-1.5 py-0.5 align-baseline text-[0.8rem] font-medium leading-snug text-white hover:bg-orange-600"
      >
        {label}
      </button>
    );
  }
  // Inactive: per-step override wins over the target's default hint.
  return (
    <button
      type="button"
      aria-disabled="true"
      title={step.disabled_hint || inactiveHint(key)}
      onClick={(e) => e.preventDefault()}
      className="mx-0.5 inline-flex cursor-not-allowed items-center rounded-md bg-gray-200 px-1.5 py-0.5 align-baseline text-[0.8rem] font-medium leading-snug text-gray-600"
    >
      {label}
    </button>
  );
}

// Renders step text (with inline HTML: <b>, <code>, links). If the step has a
// highlight target, an inline orange action button is placed at the {{target}}
// token (or appended if the token is absent).
function StepBody({ step }: { step: HelpStep }) {
  const hasTarget = !!step.highlight_target;
  if (hasTarget && step.text.includes(TARGET_TOKEN)) {
    const [before, after] = step.text.split(TARGET_TOKEN);
    return (
      <span className="help-rich">
        <span dangerouslySetInnerHTML={{ __html: before }} />
        <InlineActionButton step={step} />
        <span dangerouslySetInnerHTML={{ __html: after }} />
      </span>
    );
  }
  return (
    <span className="help-rich">
      <span dangerouslySetInnerHTML={{ __html: step.text }} />
      {hasTarget && <> <InlineActionButton step={step} /></>}
    </span>
  );
}

function StepList({ steps }: { steps: HelpStep[] }) {
  return (
    <ol className="space-y-2.5">
      {steps.map((step, i) => (
        <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-gray-700">
          <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-sky-200 text-xs font-semibold text-sky-800">
            {i + 1}
          </span>
          <span className="min-w-0">
            <StepBody step={step} />
          </span>
        </li>
      ))}
    </ol>
  );
}

function TopicDetail({ topic, topicsById }: { topic: HelpTopic; topicsById: Record<string, HelpTopic> }) {
  const [tipIdx, setTipIdx] = useState(0);
  useEffect(() => {
    if (topic.topic_type === 'tip' && topic.steps.length > 0) {
      setTipIdx(Math.floor(Math.random() * topic.steps.length));
    }
  }, [topic.topic_id, topic.topic_type, topic.steps.length]);

  return (
    <div>
      {/* Title + description card — its own block, mid-blue, white text */}
      <div className="rounded-lg bg-sky-700 px-4 py-3 shadow-sm">
        <h2 className="text-xl font-bold leading-snug text-white">{topic.title}</h2>
        {topic.short_desc && <p className="mt-1.5 text-base leading-snug text-sky-50">{topic.short_desc}</p>}
      </div>

      <div className="mt-4">
        {topic.topic_type === 'super' ? (
          <div className="space-y-5">
            {topic.children.map((id) => topicsById[id]).filter(Boolean).map((child) => (
              <div key={child.topic_id}>
                <h3 className="mb-2 text-sm font-semibold text-gray-800">{child.title}</h3>
                {child.short_desc && <p className="mb-2 text-xs text-gray-500">{child.short_desc}</p>}
                <StepList steps={child.steps} />
              </div>
            ))}
          </div>
        ) : topic.topic_type === 'tip' ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <div className="flex items-start gap-2">
              <Lightbulb className="mt-0.5 h-4 w-4 flex-none text-amber-500" />
              <div className="min-w-0 text-sm leading-relaxed text-gray-700">
                {topic.steps.length > 0 ? (
                  <StepBody step={topic.steps[tipIdx] || { text: '' }} />
                ) : (
                  <span className="text-gray-400">No tips yet.</span>
                )}
              </div>
            </div>
            {topic.steps.length > 1 && (
              <button
                type="button"
                onClick={() => setTipIdx((prev) => (prev + 1) % topic.steps.length)}
                className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-800"
              >
                <RefreshCw className="h-3 w-3" />
                Another tip
              </button>
            )}
          </div>
        ) : (
          <StepList steps={topic.steps} />
        )}
      </div>
    </div>
  );
}

export function HelpPanel() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const { isOpen, close } = useHelp();

  const [topics, setTopics] = useState<HelpTopic[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const dataLoadStarted = useRef(false);

  const [width, setWidth] = useState<number>(initialWidth);
  const [detailRatio, setDetailRatio] = useState<number>(initialRatio);
  const [isWide, setIsWide] = useState(() => window.matchMedia('(min-width: 768px)').matches);
  const contentRef = useRef<HTMLDivElement>(null);

  // Track wide vs narrow for layout (right drawer vs bottom drawer; push only when wide).
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => setIsWide(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Push the main content aside (reserve space) when open on wide screens, and
  // publish the panel width so modal/lightbox overlays can stop at the panel's
  // left edge (see `body[data-help-open]` rule in index.css) instead of covering
  // it — keeping the panel visible and clickable beside an open modal.
  useEffect(() => {
    const clear = () => {
      document.body.style.paddingRight = '';
      document.body.style.removeProperty('--help-panel-width');
      document.body.removeAttribute('data-help-open');
    };
    if (isOpen && isWide && !isAdminRoute) {
      document.body.style.paddingRight = `${width}px`;
      document.body.style.setProperty('--help-panel-width', `${width}px`);
      document.body.setAttribute('data-help-open', '');
    } else {
      clear();
    }
    return clear;
  }, [isOpen, isWide, isAdminRoute, width]);

  const loadData = useCallback(async () => {
    if (dataLoadStarted.current) return;
    dataLoadStarted.current = true;
    try {
      const [topicList, counts] = await Promise.all([api.getHelpTopics(), getProgressCounts()]);
      const sorted = [...topicList].sort(
        (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.title.localeCompare(b.title),
      );
      setTopics(sorted);
      setSelectedId((prev) => prev ?? pickDefaultTopic(sorted, counts.resumes, counts.jobs)?.topic_id ?? null);
      setLoaded(true);
    } catch {
      setLoadError(true);
      dataLoadStarted.current = false;
    }
  }, []);

  useEffect(() => { if (isOpen) void loadData(); }, [isOpen, loadData]);

  // --- drag-resize handlers ---
  const startWidthDrag = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    document.body.setAttribute('data-help-dragging', '');
    let latest = width;
    const onMove = (ev: PointerEvent) => { latest = clampWidth(window.innerWidth - ev.clientX); setWidth(latest); };
    const onUp = () => {
      document.body.removeAttribute('data-help-dragging');
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      localStorage.setItem(WIDTH_KEY, String(Math.round(latest)));
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [width]);

  const startHeightDrag = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    const rect = contentRef.current?.getBoundingClientRect();
    if (!rect) return;
    document.body.setAttribute('data-help-dragging', '');
    let latest = detailRatio;
    const onMove = (ev: PointerEvent) => {
      latest = clampRatio((ev.clientY - rect.top) / rect.height);
      setDetailRatio(latest);
    };
    const onUp = () => {
      document.body.removeAttribute('data-help-dragging');
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      localStorage.setItem(RATIO_KEY, latest.toFixed(3));
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [detailRatio]);

  const topicsById = useMemo(() => {
    const map: Record<string, HelpTopic> = {};
    for (const t of topics) map[t.topic_id] = t;
    return map;
  }, [topics]);

  const groupedIndex = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = topics.filter((t) =>
      !q ||
      t.title.toLowerCase().includes(q) ||
      t.short_desc.toLowerCase().includes(q) ||
      t.category.toLowerCase().includes(q),
    );
    const groups: { category: string; items: HelpTopic[] }[] = [];
    for (const t of filtered) {
      const cat = t.category || 'General';
      let g = groups.find((x) => x.category === cat);
      if (!g) { g = { category: cat, items: [] }; groups.push(g); }
      g.items.push(t);
    }
    return groups;
  }, [topics, search]);

  const selectedTopic = selectedId ? topicsById[selectedId] : null;

  if (isAdminRoute) return null;

  return (
    <div
      className={`fixed z-40 flex flex-col shadow-2xl ring-1 ring-black/10 transition-transform duration-300 ease-out
        inset-x-0 bottom-0 h-[70vh] rounded-t-2xl
        md:inset-x-auto md:top-0 md:right-0 md:bottom-auto md:h-full md:rounded-none
        ${isOpen ? 'translate-y-0 md:translate-x-0' : 'translate-y-full md:translate-y-0 md:translate-x-full'}`}
      style={isWide ? { width: `${width}px` } : undefined}
      role="complementary"
      aria-label="Help assistant"
      aria-hidden={!isOpen}
    >
      {/* Width divider (wide screens only) — drag to resize the panel */}
      {isWide && (
        <div
          onPointerDown={startWidthDrag}
          className="absolute left-0 top-0 h-full w-1.5 -ml-0.5 cursor-col-resize bg-transparent hover:bg-sky-300/60 active:bg-sky-400/70"
          title="Drag to resize"
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between border-b border-sky-100 bg-sky-50 px-4 py-3">
        <div className="flex items-center gap-2">
          <HelpCircle className="h-5 w-5 text-sky-500" />
          <span className="font-semibold text-gray-900">Help &amp; Tips</span>
        </div>
        <button
          type="button"
          onClick={close}
          aria-label="Close help"
          className="rounded-md p-1 text-gray-400 hover:bg-sky-100 hover:text-gray-600"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Detail + divider + index, sized by detailRatio */}
      <div ref={contentRef} className="flex min-h-0 flex-1 flex-col">
        {/* Detail (top, light blue) */}
        <div
          className="min-h-0 overflow-y-auto bg-sky-50 px-4 py-4"
          style={{ flexGrow: detailRatio, flexShrink: 1, flexBasis: 0 }}
        >
          {loadError ? (
            <div className="text-sm text-red-600">
              Couldn&apos;t load help right now.{' '}
              <button className="underline" onClick={() => { setLoadError(false); void loadData(); }}>Retry</button>
            </div>
          ) : !loaded ? (
            <div className="text-sm text-gray-400">Loading help…</div>
          ) : selectedTopic ? (
            <TopicDetail topic={selectedTopic} topicsById={topicsById} />
          ) : (
            <div className="text-sm text-gray-400">No help topics available.</div>
          )}
        </div>

        {/* Height divider — drag to resize detail vs index */}
        <div
          onPointerDown={startHeightDrag}
          className="group flex h-2 flex-none cursor-row-resize items-center justify-center bg-sky-100 hover:bg-sky-200"
          title="Drag to resize"
        >
          <div className="h-0.5 w-8 rounded-full bg-sky-400 group-hover:bg-sky-500" />
        </div>

        {/* Index (bottom, dark blue, white text) */}
        <div
          className="flex min-h-0 flex-col bg-sky-900 text-white"
          style={{ flexGrow: 1 - detailRatio, flexShrink: 1, flexBasis: 0 }}
        >
          <div className="px-3 pt-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-sky-300" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search help topics…"
                className="w-full rounded-lg border border-sky-700 bg-sky-800 py-1.5 pl-8 pr-3 text-sm text-white placeholder-sky-300 focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-400"
              />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
            {groupedIndex.length === 0 ? (
              <div className="px-1 py-2 text-sm text-sky-300">No matching topics.</div>
            ) : (
              groupedIndex.map((group) => (
                <div key={group.category} className="mb-2">
                  <div className="px-1 py-1 text-xs font-semibold uppercase tracking-wide text-sky-300">
                    {group.category}
                  </div>
                  <ul>
                    {group.items.map((t) => (
                      <li key={t.topic_id}>
                        <button
                          type="button"
                          onClick={() => setSelectedId(t.topic_id)}
                          className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition ${
                            selectedId === t.topic_id
                              ? 'bg-white/20 font-medium text-white'
                              : 'text-sky-100 hover:bg-white/10'
                          }`}
                        >
                          {t.topic_type === 'tip' && <Lightbulb className="h-3.5 w-3.5 flex-none text-amber-300" />}
                          <span className="truncate">{t.title}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
